import React, { useEffect, useMemo, useState } from 'react';
import { ArrowRight, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import { productBrands, stoneShowcaseContent } from '../data/cinematicContent';

export const CinematicStoneShowcase = () => {
  const slides = useMemo(() => stoneShowcaseContent.slides, []);
  const [activeSlide, setActiveSlide] = useState(0);

  useEffect(() => {
    const autoplayTimer = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % slides.length);
    }, 5200);

    return () => clearInterval(autoplayTimer);
  }, [slides.length]);

  const scrollToContact = () => {
    const element = document.querySelector('#contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="products" className="relative overflow-hidden bg-[#edf3f8] py-28 md:py-32">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_10%,rgba(129,159,189,0.26),transparent_38%),radial-gradient(circle_at_85%_75%,rgba(255,255,255,0.55),transparent_45%)]" />
      <div className="absolute left-0 right-0 top-0 h-16 stone-strata opacity-40" />
      <div className="absolute left-0 right-0 bottom-0 h-16 stone-strata opacity-30 rotate-180" />

      <div className="relative z-10 mx-auto grid max-w-7xl gap-10 px-6 lg:grid-cols-[1.2fr_0.8fr] lg:items-start">
        <div>
          <span className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white/75 px-4 py-2 text-xs font-semibold uppercase tracking-[0.22em] text-slate-600">
            <Sparkles className="h-3.5 w-3.5" />
            {stoneShowcaseContent.badge}
          </span>

          <h2 className="mt-5 max-w-3xl text-4xl font-bold tracking-tight text-slate-900 md:text-6xl">
            {stoneShowcaseContent.title}
          </h2>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-600 md:text-lg">
            {stoneShowcaseContent.description}
          </p>

          <div className="mt-10 overflow-hidden rounded-3xl border border-slate-200 bg-white/80 shadow-[0_22px_60px_rgba(93,118,143,0.22)]">
            <div className="relative aspect-[16/10]">
              {slides.map((slide, index) => (
                <article
                  key={slide.id}
                  className={`absolute inset-0 transition-all duration-700 ${
                    index === activeSlide
                      ? 'pointer-events-auto translate-x-0 opacity-100'
                      : index < activeSlide
                        ? 'pointer-events-none -translate-x-8 opacity-0'
                        : 'pointer-events-none translate-x-8 opacity-0'
                  }`}
                >
                  <img
                    src={slide.image}
                    alt={slide.name}
                    className="h-full w-full object-cover"
                    loading={index === 0 ? 'eager' : 'lazy'}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 via-slate-900/15 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
                    <p className="text-xs font-semibold uppercase tracking-[0.26em] text-slate-200">{slide.category}</p>
                    <h3 className="mt-2 text-2xl font-semibold text-white md:text-3xl">{slide.name}</h3>
                    <p className="mt-3 max-w-2xl text-sm text-slate-200 md:text-base">{slide.description}</p>
                  </div>
                </article>
              ))}
            </div>

            <div className="flex flex-wrap items-center justify-between gap-5 border-t border-slate-200 bg-slate-50/90 px-6 py-5">
              <div className="flex items-center gap-2">
                {slides.map((slide, index) => (
                  <button
                    key={slide.id}
                    aria-label={`View ${slide.name}`}
                    onClick={() => setActiveSlide(index)}
                    className={`h-2.5 rounded-full transition-all duration-300 ${
                      index === activeSlide ? 'w-10 bg-slate-500' : 'w-2.5 bg-slate-300 hover:bg-slate-500'
                    }`}
                  />
                ))}
              </div>

              <Button
                onClick={scrollToContact}
                className="bg-slate-600 text-slate-100 hover:bg-slate-500"
              >
                Request Material Consultation
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>

        <aside className="rounded-3xl border border-slate-200 bg-white/75 p-6 backdrop-blur-sm md:p-8">
          <p className="text-xs font-semibold uppercase tracking-[0.26em] text-slate-600">Product Lines</p>
          <h3 className="mt-3 text-2xl font-semibold text-slate-900">Available Brands</h3>
          <p className="mt-3 text-sm leading-relaxed text-slate-600">
            We source trusted solid surface and engineered stone brands for residential and commercial requirements.
          </p>

          <ul className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-1">
            {productBrands.map((brand, index) => (
              <li
                key={brand}
                className="group flex items-center justify-between rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 transition-all hover:border-slate-400 hover:bg-white"
              >
                <span className="text-sm font-medium text-slate-800">{brand}</span>
                <span className="text-xs text-slate-500 group-hover:text-slate-700">{String(index + 1).padStart(2, '0')}</span>
              </li>
            ))}
          </ul>

          <div className="mt-8 rounded-2xl border border-slate-300 bg-slate-100/80 p-4">
            <p className="text-xs uppercase tracking-[0.2em] text-slate-600">Design Tip</p>
            <p className="mt-2 text-sm text-slate-700">
              Mix one bold veined slab with a quieter neutral sheet to create contrast without overwhelming the space.
            </p>
          </div>
        </aside>
      </div>
    </section>
  );
};
